import { useQuery } from '@tanstack/react-query';
import { Geometry, Nullable } from '@tcc/common';
import { YieldRegionReport } from '../../../../types';
import { Location } from '../../../../types/Location';
import {
  fetchRegionReport,
  getGraphQlErrorMessage,
  validateRegionAgainstField,
} from './services/regionReportQueryService';

interface UseRegionValidationQueryParams {
  geometry: Geometry | Location | undefined;
  geometryHash: string | undefined;
  fieldBoundary: unknown;
  fieldUUID: string;
  isValidationEnabled: boolean;
}

const useRegionValidationQuery = ({
  geometry,
  geometryHash,
  fieldBoundary,
  fieldUUID,
  isValidationEnabled,
}: UseRegionValidationQueryParams) => useQuery({
    queryKey: ['frr-region-validity', fieldUUID, geometryHash],
    queryFn: () => validateRegionAgainstField(fieldBoundary, geometry),
    enabled: isValidationEnabled,
    retry: false,
    staleTime: 0,
  });

interface UseReportQueryParams {
  fieldUUID: string;
  frrSeasonTag: string;
  geometry: Geometry | Location | undefined;
  geometryHash: string | undefined;
  reportQueryEnabled: boolean;
  lastReportGraphQlErrorRef: React.MutableRefObject<string | undefined>;
}

const useReportQuery = ({
  fieldUUID,
  frrSeasonTag,
  geometry,
  geometryHash,
  reportQueryEnabled,
  lastReportGraphQlErrorRef,
}: UseReportQueryParams) => useQuery({
    queryKey: [
      'frr-region-report',
      fieldUUID,
      frrSeasonTag,
      geometryHash,
    ],
    queryFn: () => fetchRegionReport({
      fieldUUID,
      seasonTag: frrSeasonTag,
      geometry,
      onResponse: (response) => {
        lastReportGraphQlErrorRef.current = getGraphQlErrorMessage(response);
      },
    }),
    enabled: reportQueryEnabled,
    refetchOnMount: false,
    retry: 1,
    staleTime: 0,
  });

export interface UseRegionReportQueriesParams {
  geometry: Geometry | Location | undefined;
  geometryHash: string | undefined;
  fieldBoundary: unknown;
  fieldUUID: string;
  isValidationEnabled: boolean;
  hasHarvestData: boolean;
  frrSeasonTag: string;
  lastReportGraphQlErrorRef: React.MutableRefObject<string | undefined>;
}

export interface UseRegionReportQueriesResult {
  isRegionValid: Nullable<boolean>;
  regionValidationError?: Error;
  hasRegionValidationError: boolean;
  reportData: Nullable<YieldRegionReport>;
  isLoadingReport: boolean;
  isReportSuccess: boolean;
  isReportFetched: boolean;
  reportError?: Error;
  hasReportError: boolean;
  reportDataUpdatedAt: number;
}

export const useRegionReportQueries = ({
  geometry,
  geometryHash,
  fieldBoundary,
  fieldUUID,
  isValidationEnabled,
  hasHarvestData,
  frrSeasonTag,
  lastReportGraphQlErrorRef,
}: UseRegionReportQueriesParams): UseRegionReportQueriesResult => {
  const regionValidationQuery = useRegionValidationQuery({
    geometry,
    geometryHash,
    fieldBoundary,
    fieldUUID,
    isValidationEnabled,
  });

  const isRegionValid = isValidationEnabled
    ? (regionValidationQuery.data ?? null)
    : null;

  const reportQueryEnabled = Boolean(
    hasHarvestData
    && isRegionValid
    && fieldUUID
    && frrSeasonTag
    && geometry
  );

  const reportQuery = useReportQuery({
    fieldUUID,
    frrSeasonTag,
    geometry,
    geometryHash,
    reportQueryEnabled,
    lastReportGraphQlErrorRef,
  });

  const reportData = (reportQuery.data as Nullable<YieldRegionReport>) ?? null;
  const reportError = reportQuery.error as Error | undefined;
  const regionValidationError = regionValidationQuery.error as Error | undefined;
  const isValidationLoading = Boolean(
    isValidationEnabled
    && regionValidationQuery.isLoading
    && isRegionValid === null
  );
  const isReportLoading = Boolean(
    reportQueryEnabled
    && (reportQuery.isLoading || (reportQuery.isFetching && !reportData))
  );
  const isLoadingReport = isValidationLoading || isReportLoading;


  return {
    isRegionValid,
    regionValidationError,
    hasRegionValidationError: Boolean(regionValidationError),
    reportData,
    isLoadingReport,
    isReportSuccess: reportQuery.isSuccess,
    isReportFetched: reportQuery.isFetched,
    reportError,
    hasReportError: Boolean(reportError),
    reportDataUpdatedAt: reportQuery.dataUpdatedAt,
  };
};
